import React from 'react';
import { useNavigate } from 'react-router-dom';
import { History, LogOut, Menu, Search, Settings, Shield, X } from 'lucide-react';
import type { Agent, Conversation, Message } from '../../types';
import { cn, formatShortcut, isAdminOrAbove, ui } from '../../utils';
import { useAuthStore, type User } from '../../stores/authStore';
import { LoadingFallback } from '../Common/LoadingFallback';

const SearchModal = React.lazy(() => import('./SearchModal').then((m) => ({ default: m.default })));
const SettingsModal = React.lazy(() => import('./SettingsModal').then((m) => ({ default: m.SettingsModal })));

interface HeaderProps {
  onMenuClick: () => void;
  isSidebarOpen: boolean;
  agents: Agent[];
  conversations: Conversation[];
  messagesByConversation: Record<string, Message[]>;
  onAgentSelect: (agentId: string) => void;
  onConversationSelect: (conversationId: string, messageId?: string) => void;
  className?: string;
}

export function Header({
  onMenuClick,
  isSidebarOpen,
  agents,
  conversations,
  messagesByConversation,
  onAgentSelect,
  onConversationSelect,
  className,
}: HeaderProps) {
  const navigate = useNavigate();
  const realUser = useAuthStore((state) => state.user);
  const impersonatedUser = useAuthStore((state) => state.impersonatedUser);
  const clearImpersonation = useAuthStore((state) => state.clearImpersonation);
  const viewAsUser = useAuthStore((state) => state.viewAsUser);
  const clearViewAs = useAuthStore((state) => state.clearViewAs);
  const logout = useAuthStore((state) => state.logout);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
  const activeUser = impersonatedUser || realUser;
  const isImpersonating = Boolean(impersonatedUser);
  const searchShortcut = formatShortcut(['Mod', 'K']);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  React.useEffect(() => {
    if (isImpersonating) {
      setIsSettingsOpen(false);
    }
  }, [isImpersonating]);

  const canAccessAdmin = isAdminOrAbove(activeUser?.roles);

  return (
    <>
      <div
        className={cn(
          'sticky top-0 z-30 flex flex-shrink-0 flex-col border-b border-border bg-card backdrop-blur',
          className,
        )}
      >
        {isImpersonating ? (
          <div className="bg-amber-600 px-4 py-2 text-center text-xs text-white">
            <div className="flex items-center justify-center gap-2">
              <span>Viewing as: {impersonatedUser?.name || impersonatedUser?.email}</span>
              <button
                type="button"
                onClick={clearImpersonation}
                className="rounded-full px-2 py-1 font-medium underline-offset-2 hover:underline"
                aria-label="Exit impersonation"
              >
                Exit impersonation
              </button>
            </div>
          </div>
        ) : null}

        {viewAsUser ? (
          <div className="bg-sky-700 px-4 py-2 text-center text-xs text-white">
            <div className="flex items-center justify-center gap-2">
              <span>Viewing chats of: {viewAsUser.name || viewAsUser.email} (read-only)</span>
              <button
                type="button"
                onClick={clearViewAs}
                className="rounded-full px-2 py-1 font-medium underline-offset-2 hover:underline"
                aria-label="Exit read-only view"
              >
                Exit view
              </button>
            </div>
          </div>
        ) : null}

        <header className="flex min-h-[60px] items-center justify-between gap-4 px-4 py-3 sm:px-5">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onMenuClick}
            className={cn(ui.button.icon, 'lg:hidden')}
            aria-label="Toggle menu"
          >
            {isSidebarOpen ? <X className="h-5 w-5 text-muted-foreground" /> : <Menu className="h-5 w-5 text-muted-foreground" />}
          </button>

          <div className="flex items-center gap-3">
            <img src="/ally-icon.png" alt="AFN Ally" className="h-10 w-10 rounded-2xl shadow-sm shadow-black/20" />
            <h1 className="text-lg font-semibold text-foreground-bright">AFN Ally</h1>
          </div>
        </div>

        <div className="flex items-center">
          {activeUser ? (
            <UserAvatar
              user={activeUser}
              canAccessAdmin={canAccessAdmin}
              isImpersonating={isImpersonating}
              canAccessSettings={!isImpersonating}
              onLogout={logout}
              onOpenSearch={() => setIsSearchOpen(true)}
              onOpenSettings={() => setIsSettingsOpen(true)}
              onOpenAdmin={() => navigate('/admin')}
              onOpenHistory={() => navigate('/history')}
              onExitImpersonation={clearImpersonation}
              searchShortcut={searchShortcut}
            />
          ) : null}
        </div>
        </header>
      </div>

      {isSearchOpen ? (
        <React.Suspense fallback={<LoadingFallback />}>
          <SearchModal
            isOpen={isSearchOpen}
            onClose={() => setIsSearchOpen(false)}
            agents={agents}
            conversations={conversations}
            messagesByConversation={messagesByConversation}
            onAgentSelect={onAgentSelect}
            onConversationSelect={onConversationSelect}
          />
        </React.Suspense>
      ) : null}

      {isSettingsOpen && !isImpersonating ? (
        <React.Suspense fallback={<LoadingFallback />}>
          <SettingsModal isOpen={isSettingsOpen && !isImpersonating} onClose={() => setIsSettingsOpen(false)} />
        </React.Suspense>
      ) : null}
    </>
  );
}

function UserAvatar({
  user,
  canAccessAdmin,
  isImpersonating,
  canAccessSettings,
  onLogout,
  onOpenSearch,
  onOpenSettings,
  onOpenAdmin,
  onOpenHistory,
  onExitImpersonation,
  searchShortcut,
}: {
  user: User;
  canAccessAdmin: boolean;
  isImpersonating: boolean;
  canAccessSettings: boolean;
  onLogout: () => void;
  onOpenSearch: () => void;
  onOpenSettings: () => void;
  onOpenAdmin: () => void;
  onOpenHistory: () => void;
  onExitImpersonation: () => void;
  searchShortcut: string;
}) {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const initials = user?.name?.charAt(0)?.toUpperCase() || 'U';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsDropdownOpen((open) => !open)}
        className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-blue-600 text-sm font-semibold text-white shadow-sm shadow-emerald-950/30 transition-all duration-150 hover:shadow-lg"
        aria-label="User menu"
        aria-haspopup="menu"
        aria-expanded={isDropdownOpen}
      >
        {initials}
      </button>

      {isDropdownOpen ? (
        <div className="absolute right-0 top-12 z-50 w-72 rounded-[1.25rem] border border-border bg-card isolate opacity-100 backdrop-blur-none py-2 shadow-2xl shadow-black/30" role="menu" aria-label="User menu">
          <div className="border-b border-border px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-blue-600 font-semibold text-white">
                {initials}
              </div>
              <div>
                <div className="text-sm font-medium text-foreground-bright">{user.name || 'User'}</div>
                <div className="text-xs text-muted-foreground">{user.email}</div>
              </div>
            </div>
          </div>

          <div className="py-1">
            <button
              type="button"
              onClick={() => {
                setIsDropdownOpen(false);
                onOpenSearch();
              }}
              className="flex min-h-[44px] w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
              role="menuitem"
              aria-label="Search conversations"
            >
              <span className="flex items-center gap-2">
                <Search className="h-4 w-4" />
                Search
              </span>
              <span className="rounded border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground">{searchShortcut}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setIsDropdownOpen(false);
                onOpenHistory();
              }}
              className="flex min-h-[44px] w-full items-center gap-2 px-4 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
              role="menuitem"
              aria-label="Open history"
            >
              <History className="h-4 w-4" />
              History
            </button>
            {canAccessSettings ? (
              <button
                type="button"
                onClick={() => {
                  setIsDropdownOpen(false);
                  onOpenSettings();
                }}
                className="flex min-h-[44px] w-full items-center gap-2 px-4 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                role="menuitem"
                aria-label="Open settings"
              >
                <Settings className="h-4 w-4" />
                Settings
              </button>
            ) : null}
            {canAccessAdmin ? (
              <button
                type="button"
                onClick={() => {
                  setIsDropdownOpen(false);
                  onOpenAdmin();
                }}
                className="flex min-h-[44px] w-full items-center gap-2 px-4 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                role="menuitem"
                aria-label="Open admin"
              >
                <Shield className="h-4 w-4" />
                Admin
              </button>
            ) : null}
            {isImpersonating ? (
              <button
                type="button"
                onClick={() => {
                  setIsDropdownOpen(false);
                  onExitImpersonation();
                }}
                className="flex min-h-[44px] w-full items-center gap-2 px-4 py-2 text-left text-sm text-amber-300 transition-colors duration-150 hover:bg-amber-500/10"
                role="menuitem"
                aria-label="Exit impersonation"
              >
                <X className="h-4 w-4" />
                Exit Impersonation
              </button>
            ) : null}
            <div className="my-1 border-t border-border" />
            <button
              type="button"
              onClick={() => {
                setIsDropdownOpen(false);
                onLogout();
              }}
              className="flex min-h-[44px] w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-300 transition-colors duration-150 hover:bg-red-500/10"
              role="menuitem"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Header;
